import { Component, OnInit, inject } from "@angular/core";
import { TpaConnectionDataService } from "@app/external-connections/services/tpa-connection.data.service";
import { Pages } from "@constants/pages/Pages";
import { FormatType } from "@enums/FormatType";
import { ModuleType } from "@enums/ModuleType";
import { RendererType } from "@enums/RendererType";
import { nameof } from "@methods/CommonMethods";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { TabService } from "@services/tabs/TabService";
import { TpaRequestModel } from "../models/tpaRequestModel";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { TranslocoService, provideTranslocoScope } from "@jsverse/transloco";
import { firstValueFrom } from "rxjs";

@Component({
    selector: "app-tpa-request-overview",
    templateUrl: "./tpa-request-overview.component.html",
    styleUrls: ["./tpa-request-overview.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope("external-connections")],
})
export class TpaRequestOverviewComponent implements OnInit {
    private _tabs = inject(TabService);
    pages = inject(Pages);
    private _ds = inject(TpaConnectionDataService);
    private _transloco = inject(TranslocoService);

    moduleType: ModuleType = ModuleType.EC;
    tabID: string;
    tab: TabModel;
    data: Promise<TpaRequestModel[]>;
    headerLookup: any = {};
    filterConfigs: FilterConfig[] = [];
    rendererConfig: RendererConfig[] = [];

    dateProps: string[] = ["startDate", "endDate", "requestDate"];

    permissions: WorkspaceModulePermissionModel = {
        canAdd: false,
        canOpen: true,

        isModuleAdmin: false,
        canPublish: false,
        canSetSystemDefault: false,
    };

    async ngOnInit(): Promise<void> {
        await firstValueFrom(this._transloco.load(`external-connections/${this._transloco.getActiveLang()}`));
        this.setRendererConfig();
        this.mapHeaders();
        this.data = firstValueFrom(this._ds.getRequests());
    }

    setRendererConfig(): void {
        ["startDate", "endDate"].forEach((dateProp) => {
            const config = <RendererConfig>{
                textColumn: dateProp,
                type: RendererType.Default,
                formatType: FormatType.DateFormat,
            };

            this.rendererConfig.push(config);
        });

        const requestDateConfig = <RendererConfig>{
            textColumn: "requestDate",
            type: RendererType.Default,
            formatType: FormatType.DateTimeFormat,
        };

        this.rendererConfig.push(requestDateConfig);
    }

    openDetail(request: TpaRequestModel): void {
        this.openTab(request.requestID, request.connectionName);
    }

    private openTab(guid: string, name: string): void {
        this._tabs.generateTab(this.pages.TpaRequestDetail, guid, name);
    }

    private mapHeaders(): void {
        const headerLookup = {};
        headerLookup[nameof<TpaRequestModel>("requestID")] = this._transloco.translate("externalConnections.overview.requestID");
        headerLookup[nameof<TpaRequestModel>("requestDate")] = this._transloco.translate("externalConnections.overview.requestDate");
        headerLookup[nameof<TpaRequestModel>("connectionName")] = this._transloco.translate("externalConnections.overview.connectionName");
        headerLookup[nameof<TpaRequestModel>("startDate")] = this._transloco.translate("externalConnections.overview.startDate");
        headerLookup[nameof<TpaRequestModel>("endDate")] = this._transloco.translate("externalConnections.overview.endDate");
        headerLookup[nameof<TpaRequestModel>("requestedTestType")] = this._transloco.translate("externalConnections.overview.requestedTestType");
        headerLookup[nameof<TpaRequestModel>("id")] = "id";
        headerLookup[nameof<TpaRequestModel>("status")] = this._transloco.translate("externalConnections.overview.status");
        this.headerLookup = headerLookup;
    }
}
